import {
  AdminInvestmentTransactionsSyncOutcome,
  FinancialAccountPrimaryType,
  InvestmentHoldingsSyncOutcome,
} from '../../__generated__/globalTypes';

/** The Plaid product name as it appears in a connection's consent list. */
export const INVESTMENTS_PRODUCT = 'investments';

export interface IHoldingsSyncOutcomeInput {
  outcome: InvestmentHoldingsSyncOutcome;
  providerErrorCode?: string | null;
  accountsSynced?: number | null;
  holdingsRecorded?: number | null;
}

export interface ISyncOutcomeToast {
  type: 'success' | 'error';
  message: string;
}

/** Shown when the request itself failed, which says nothing about whether the pull was handed off. */
export const INVESTMENT_TRANSACTIONS_REQUEST_FAILED_MESSAGE =
  'Investment transactions request failed. The pull may still have been handed off: check "Transactions synced" in a few minutes before asking again.';

const NO_INVESTMENT_ACCOUNTS_MESSAGE = 'No investment accounts on this connection.';
const NOT_CONSENTED_MESSAGE = 'Not consented to investments — relink candidate.';
const INSTITUTION_LACKS_PRODUCT_MESSAGE = 'Institution does not offer investments.';
const NO_CONNECTION_MESSAGE = 'Bank connection not found.';

export interface IConnectionAccount {
  type: FinancialAccountPrimaryType;
  /** Set when the household hid the account; the holdings step reads only visible accounts. */
  ignoredAt?: string | null;
}

/** Whether the connection holds a visible investment account, which is what the holdings step needs. */
export function hasInvestmentAccount(accounts: readonly IConnectionAccount[]): boolean {
  return accounts.some((account) => account.type === FinancialAccountPrimaryType.Investment && !account.ignoredAt);
}

/**
 * A connection whose recorded consent lacks investments while it holds an
 * investment account can only get holdings by relinking. Null consent means
 * no account fetch has recorded it yet, which is not a verdict.
 */
export function isInvestmentsRelinkCandidate(
  accounts: readonly IConnectionAccount[],
  consentedProducts: readonly string[] | null
): boolean {
  return (
    hasInvestmentAccount(accounts) && consentedProducts !== null && !consentedProducts.includes(INVESTMENTS_PRODUCT)
  );
}

/** The toast an operator reads after a forced holdings sync, naming the leg that refused. */
export function describeHoldingsSyncOutcome(result: IHoldingsSyncOutcomeInput): ISyncOutcomeToast {
  switch (result.outcome) {
    case InvestmentHoldingsSyncOutcome.Synced: {
      const accounts = result.accountsSynced ?? 0;
      const positions = result.holdingsRecorded ?? 0;
      return {
        type: 'success',
        message: `Holdings synced: ${accounts} ${accounts === 1 ? 'account' : 'accounts'}, ${positions} ${positions === 1 ? 'position' : 'positions'}.`,
      };
    }
    case InvestmentHoldingsSyncOutcome.NoInvestmentAccounts:
      return { type: 'error', message: NO_INVESTMENT_ACCOUNTS_MESSAGE };
    case InvestmentHoldingsSyncOutcome.NotConsented:
      return { type: 'error', message: NOT_CONSENTED_MESSAGE };
    case InvestmentHoldingsSyncOutcome.InstitutionLacksProduct:
      return { type: 'error', message: INSTITUTION_LACKS_PRODUCT_MESSAGE };
    case InvestmentHoldingsSyncOutcome.ProductNotReady:
      return { type: 'error', message: 'Provider is still preparing holdings; its webhook follows.' };
    case InvestmentHoldingsSyncOutcome.ProviderRefused:
      return { type: 'error', message: `Provider refused holdings: ${result.providerErrorCode ?? 'unknown code'}.` };
    case InvestmentHoldingsSyncOutcome.NoConnection:
      return { type: 'error', message: NO_CONNECTION_MESSAGE };
    default:
      return { type: 'error', message: `Holdings sync ended with ${String(result.outcome)}.` };
  }
}

/**
 * The toast an operator reads after asking for an investment transactions
 * pull. The pull finishes after the request returns, so success here means
 * handed off, and the toast says where to read that it finished.
 */
export function describeInvestmentTransactionsSyncOutcome(
  outcome: AdminInvestmentTransactionsSyncOutcome
): ISyncOutcomeToast {
  switch (outcome) {
    case AdminInvestmentTransactionsSyncOutcome.Dispatched:
      return {
        type: 'success',
        message:
          'Investment transactions pull started. Reload in a few minutes: "Transactions synced" moves when it finishes.',
      };
    case AdminInvestmentTransactionsSyncOutcome.NoInvestmentAccounts:
      return { type: 'error', message: NO_INVESTMENT_ACCOUNTS_MESSAGE };
    case AdminInvestmentTransactionsSyncOutcome.NotConsented:
      return { type: 'error', message: NOT_CONSENTED_MESSAGE };
    case AdminInvestmentTransactionsSyncOutcome.InstitutionLacksProduct:
      return { type: 'error', message: INSTITUTION_LACKS_PRODUCT_MESSAGE };
    case AdminInvestmentTransactionsSyncOutcome.NoConnection:
      return { type: 'error', message: NO_CONNECTION_MESSAGE };
    default:
      return { type: 'error', message: `Investment transactions sync ended with ${String(outcome)}.` };
  }
}
